import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SchemaMarkup from "@/components/SchemaMarkup";
import SEOMeta from "@/components/SEOMeta";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { 
  Home, 
  Building, 
  Layers, 
  Ruler, 
  MapPin, 
  Phone, 
  Mail, 
  CheckCircle, 
  Star, 
  Users, 
  Award,
  Shield,
  Clock,
  Palette,
  Settings
} from "lucide-react";

// Import flooring images
import flooringInstallation from "@/assets/flooring-installation.jpg";
import hardwoodFlooringInstall from "@/assets/hardwood-flooring-install.jpg";
import vinylFlooringInstall from "@/assets/vinyl-flooring-install.jpg";
import tileFlooringInstall from "@/assets/tile-flooring-install.jpg";
import carpetFlooringInstall from "@/assets/carpet-flooring-install.jpg";
import ceramicTileInstallation from "@/assets/ceramic-tile-installation.jpg";
import marbleTileFlooring from "@/assets/marble-tile-flooring.jpg";
import porcelainTileKitchen from "@/assets/porcelain-tile-kitchen.jpg";

const FlooringServices = () => {
  const faqData = [
    {
      question: "Do you install ceramic and porcelain tile in Toronto?",
      answer: "Yes. Blue Knight Contracting provides professional ceramic, porcelain, and natural stone tile installation throughout Toronto, Hamilton, Niagara Region, and the GTA for residential and commercial properties."
    },
    {
      question: "What types of flooring do you install?",
      answer: "We install all types of flooring including hardwood, laminate, luxury vinyl plank (LVP), ceramic tile, porcelain tile, marble, granite, natural stone, carpet, and specialty flooring for residential and commercial properties across Ontario."
    },
    {
      question: "Do you install marble and natural stone flooring?",
      answer: "Absolutely. Our skilled flooring team specializes in marble, granite, travertine, slate, and limestone installation with expert sealing and finishing for long-lasting beauty."
    },
    {
      question: "Do you provide flooring removal and subfloor preparation?",
      answer: "Yes. We provide complete flooring services including removal of existing flooring, subfloor preparation, moisture testing, leveling, and installation of new flooring materials."
    },
    {
      question: "Do you offer flooring warranties?",
      answer: "Yes. We provide warranties on both our installation workmanship and can offer manufacturer warranties on flooring materials, ensuring your investment is protected."
    },
    {
      question: "Can you install heated floors under tile?",
      answer: "Yes. We specialize in radiant floor heating installation under ceramic, porcelain, and stone tile flooring for comfort and energy efficiency in bathrooms, kitchens, and throughout your home."
    }
  ];

  const serviceData = {
    "@type": "Service",
    name: "Flooring Installation Services Toronto, Hamilton, Niagara",
    description: "Professional flooring installation services across Toronto, Hamilton, Niagara Region and GTA including hardwood, ceramic tile, porcelain, marble, luxury vinyl and carpet installation for residential and commercial properties.",
    provider: {
      "@type": "Organization",
      name: "Blue Knight Contracting and Construction"
    },
    serviceType: "Flooring Installation Services",
    areaServed: "Ontario, Canada"
  };

  const flooringServices = [
    {
      title: "Hardwood Flooring",
      description: "Solid hardwood and engineered wood flooring installation with professional finishing and refinishing services.",
      icon: Layers,
      types: [
        "Solid hardwood installation",
        "Engineered wood flooring",
        "Hardwood refinishing",
        "Floor sanding and staining",
        "Custom wood species",
        "Hardwood repair and restoration"
      ]
    },
    {
      title: "Luxury Vinyl & Laminate",
      description: "High-quality vinyl plank and laminate flooring installation for durable, attractive floors.",
      icon: Settings,
      types: [
        "Luxury vinyl plank (LVP)",
        "Luxury vinyl tile (LVT)",
        "Laminate flooring",
        "Waterproof vinyl flooring",
        "Click-lock installation",
        "Glue-down vinyl systems"
      ]
    },
    {
      title: "Tile & Stone Flooring",
      description: "Ceramic, porcelain, marble, and natural stone tile installation for elegant, durable flooring.",
      icon: Ruler,
      types: [
        "Ceramic tile installation",
        "Porcelain tile flooring",
        "Marble and granite flooring",
        "Natural stone (travertine, slate, limestone)",
        "Mosaic and decorative tiles",
        "Large format tile installation",
        "Heated floor systems",
        "Tile backsplash installation"
      ]
    },
    {
      title: "Carpet Installation",
      description: "Professional carpet installation for residential and commercial spaces with various carpet types.",
      icon: Home,
      types: [
        "Residential carpet installation",
        "Commercial carpet systems",
        "Carpet tile installation",
        "Stair carpet installation",
        "Custom carpet binding",
        "Carpet removal and disposal"
      ]
    },
    {
      title: "Commercial Flooring",
      description: "Specialized commercial flooring solutions for offices, retail, healthcare, and industrial facilities.",
      icon: Building,
      types: [
        "Office flooring systems",
        "Retail flooring installation",
        "Healthcare facility flooring",
        "Industrial flooring solutions",
        "Anti-slip safety flooring",
        "High-traffic commercial floors"
      ]
    },
    {
      title: "Specialty Flooring",
      description: "Unique flooring solutions including cork, bamboo, concrete polishing, and custom installations.",
      icon: Palette,
      types: [
        "Cork flooring installation",
        "Bamboo flooring systems",
        "Concrete polishing",
        "Epoxy floor coatings",
        "Rubber flooring installation",
        "Custom flooring solutions"
      ]
    }
  ];

  const serviceAreas = [
    "Greater Toronto Area",
    "Toronto", "Mississauga", "Brampton", "Vaughan", "Markham", "Richmond Hill", "Oakville", "Burlington", "Milton",
    "Niagara Region", 
    "Niagara Falls", "St. Catharines", "Welland", "Niagara-on-the-Lake", "Fort Erie", "Grimsby", "Lincoln", "Pelham", "Port Colborne", "Thorold",
    "Eastern Ontario",
    "Ottawa", "Kingston", "Cornwall", "Belleville", "Pembroke", "Petawawa",
    "Southwestern Ontario", 
    "London", "Windsor", "Kitchener", "Waterloo", "Cambridge", "Guelph", "Sarnia", "Chatham",
    "Northern Ontario",
    "Sudbury", "Thunder Bay", "North Bay", "Sault Ste. Marie", "Timmins", "Kenora"
  ];

  return (
    <>
      <SEOMeta 
        title="Flooring Contractor — Hardwood, Tile & Vinyl | Niagara • Hamilton • GTA"
        description="Precision flooring installation in hardwood, tile and vinyl across Niagara, Hamilton & the GTA. Clean work, tight timelines, great finish."
        keywords="flooring contractor Niagara, Hamilton floor install, GTA flooring service, hardwood floors, tile installation, vinyl flooring, laminate"
        canonicalUrl="https://blueknightcontracting.ca/services/flooring"
      />
      <SchemaMarkup 
        type="Service"
        data={{
          ...serviceData,
          mainEntity: {
            "@type": "FAQPage",
            mainEntity: faqData.map(faq => ({
              "@type": "Question",
              name: faq.question,
              acceptedAnswer: {
                "@type": "Answer",
                text: faq.answer
              }
            }))
          }
        }}
      />
      
      <div className="min-h-screen bg-background">
        <Header />
        
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-primary to-primary/80 text-white py-20">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h1 className="text-4xl lg:text-6xl font-bold mb-6">
                  Expert Flooring Installation in Toronto, Hamilton & Niagara
                </h1>
                <p className="text-xl mb-8 opacity-90">
                  Blue Knight Contracting provides professional flooring installation across the GTA, Hamilton, and Niagara Region. From hardwood and luxury vinyl to ceramic tile, porcelain, marble, and natural stone, we deliver expert craftsmanship with premium materials for beautiful, long-lasting floors.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button asChild size="lg" variant="secondary">
                    <Link to="/contact">Get Free Quote</Link>
                  </Button>
                  <Button asChild size="lg" variant="outline" className="text-white bg-primary border-white hover:bg-white hover:text-primary">
                    <Link to="/services">View All Services</Link>
                  </Button>
                </div>
              </div>
              <div className="relative">
                <img 
                  src={hardwoodFlooringInstall} 
                  alt="Professional flooring contractor installing hardwood floors in Ontario home"
                  className="rounded-lg shadow-2xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Services Overview */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Complete Flooring Installation Services
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Our experienced flooring installers handle all types of flooring materials with precision and attention to detail, ensuring beautiful results that last.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {flooringServices.map((service, index) => (
                <Card key={index} className="h-full">
                  <CardHeader>
                    <service.icon className="h-12 w-12 text-primary mb-4" />
                    <CardTitle>{service.title}</CardTitle>
                    <CardDescription>{service.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {service.types.map((type, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          <span className="text-sm">{type}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Projects */}
        <section className="py-20 bg-muted/50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Featured Flooring Projects
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Explore our portfolio of flooring installations across Ontario
              </p>
            </div>

            <div className="grid lg:grid-cols-2 xl:grid-cols-4 gap-8 mb-12">
              <Card className="overflow-hidden">
                <div className="aspect-[4/3]">
                  <img 
                    src={hardwoodFlooringInstall} 
                    alt="Ontario flooring contractor installing hardwood floors in residential home"
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-2">Hardwood Installation</h3>
                  <p className="text-muted-foreground">
                    Premium hardwood flooring installation
                  </p>
                </CardContent>
              </Card>

              <Card className="overflow-hidden">
                <div className="aspect-[4/3]">
                  <img 
                    src={vinylFlooringInstall} 
                    alt="Ontario flooring installer laying luxury vinyl plank flooring"
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-2">Luxury Vinyl</h3>
                  <p className="text-muted-foreground">
                    Waterproof luxury vinyl plank installation
                  </p>
                </CardContent>
              </Card>

              <Card className="overflow-hidden">
                <div className="aspect-[4/3]">
                  <img 
                    src={tileFlooringInstall} 
                    alt="Professional tile installation work in Ontario bathroom"
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-2">Tile Installation</h3>
                  <p className="text-muted-foreground">
                    Precision ceramic and porcelain tile work
                  </p>
                </CardContent>
              </Card>

              <Card className="overflow-hidden">
                <div className="aspect-[4/3]">
                  <img 
                    src={carpetFlooringInstall} 
                    alt="Commercial carpet installation in Ontario office building"
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-2">Commercial Carpet</h3>
                  <p className="text-muted-foreground">
                    Professional commercial carpet systems
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Tile & Stone Showcase */}
            <div className="space-y-12">
              <div className="text-center">
                <h3 className="text-2xl font-bold mb-4">Ceramic, Porcelain & Natural Stone Tile Flooring</h3>
                <p className="text-muted-foreground max-w-2xl mx-auto">
                  Expert tile installation for kitchens, bathrooms, entryways, and commercial spaces
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-8">
                <Card className="overflow-hidden">
                  <div className="aspect-[4/3]">
                    <img 
                      src={ceramicTileInstallation} 
                      alt="Professional ceramic tile installation in Toronto home"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <CardContent className="p-6">
                    <h4 className="text-xl font-semibold mb-2">Ceramic & Porcelain Tile</h4>
                    <p className="text-muted-foreground">
                      Durable, water-resistant ceramic and porcelain tile installation for floors, walls, and backsplashes.
                    </p>
                  </CardContent>
                </Card>

                <Card className="overflow-hidden">
                  <div className="aspect-[4/3]">
                    <img 
                      src={marbleTileFlooring} 
                      alt="Luxury marble tile flooring installation in Hamilton home"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <CardContent className="p-6">
                    <h4 className="text-xl font-semibold mb-2">Marble & Granite Flooring</h4>
                    <p className="text-muted-foreground">
                      Elegant natural stone flooring with expert sealing and finishing for timeless beauty.
                    </p>
                  </CardContent>
                </Card>

                <Card className="overflow-hidden">
                  <div className="aspect-[4/3]">
                    <img 
                      src={porcelainTileKitchen} 
                      alt="Large format porcelain tile flooring in Niagara Region kitchen"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <CardContent className="p-6">
                    <h4 className="text-xl font-semibold mb-2">Large Format Tile</h4>
                    <p className="text-muted-foreground">
                      Modern large format porcelain and ceramic tiles for contemporary spaces with fewer grout lines.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Our Flooring Installation Process
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Professional flooring installation following industry best practices
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <Card className="text-center p-6">
                <div className="w-16 h-16 bg-primary text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  1
                </div>
                <h3 className="text-xl font-semibold mb-2">Consultation & Design</h3>
                <p className="text-muted-foreground">
                  Free consultation to select the perfect flooring for your space and budget
                </p>
              </Card>

              <Card className="text-center p-6">
                <div className="w-16 h-16 bg-primary text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  2
                </div>
                <h3 className="text-xl font-semibold mb-2">Site Preparation</h3>
                <p className="text-muted-foreground">
                  Subfloor assessment, removal of existing flooring, and surface preparation
                </p>
              </Card>

              <Card className="text-center p-6">
                <div className="w-16 h-16 bg-primary text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  3
                </div>
                <h3 className="text-xl font-semibold mb-2">Professional Installation</h3>
                <p className="text-muted-foreground">
                  Expert installation using proper techniques and quality materials
                </p>
              </Card>

              <Card className="text-center p-6">
                <div className="w-16 h-16 bg-primary text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  4
                </div>
                <h3 className="text-xl font-semibold mb-2">Final Inspection</h3>
                <p className="text-muted-foreground">
                  Quality inspection and cleanup, with care instructions provided
                </p>
              </Card>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-20 bg-muted/50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Why Choose Blue Knight Flooring?
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <Card className="text-center p-6">
                <Users className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Expert Installers</h3>
                <p className="text-muted-foreground">
                  Experienced flooring professionals with specialized training
                </p>
              </Card>

              <Card className="text-center p-6">
                <Award className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Premium Materials</h3>
                <p className="text-muted-foreground">
                  Quality flooring materials from trusted manufacturers
                </p>
              </Card>

              <Card className="text-center p-6">
                <Shield className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Installation Warranty</h3>
                <p className="text-muted-foreground">
                  Comprehensive warranty on workmanship and materials
                </p>
              </Card>

              <Card className="text-center p-6">
                <Star className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Ontario Wide</h3>
                <p className="text-muted-foreground">
                  Serving communities across Ontario with reliable service
                </p>
              </Card>
            </div>
          </div>
        </section>

        {/* Service Areas */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Flooring Installation Across Ontario
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Blue Knight Contracting provides professional flooring installation throughout Ontario, including the GTA and Niagara region
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {serviceAreas.map((area, index) => {
                const isRegionHeader = area.includes("Ontario") || area.includes("Region") || area.includes("Area");
                
                return (
                  <Card key={index} className={`p-4 text-center ${isRegionHeader ? 'bg-primary text-white col-span-full sm:col-span-2 lg:col-span-4' : ''}`}>
                    <MapPin className={`h-6 w-6 mx-auto mb-2 ${isRegionHeader ? 'text-white' : 'text-primary'}`} />
                    <p className={`font-medium ${isRegionHeader ? 'text-white text-lg' : ''}`}>{area}</p>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-muted/50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Frequently Asked Questions
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Common questions about our flooring installation services in Ontario
              </p>
            </div>

            <div className="max-w-4xl mx-auto space-y-8">
              {faqData.map((faq, index) => (
                <Card key={index} className="p-6">
                  <h3 className="text-xl font-semibold mb-3 text-primary">
                    {faq.question}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {faq.answer}
                  </p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-primary text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Ready for Beautiful New Floors?
            </h2>
            <p className="text-xl mb-8 opacity-90 max-w-3xl mx-auto">
              Contact Blue Knight Contracting today for professional flooring installation services across Ontario. Get your free consultation and flooring quote.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
              <div className="flex items-center gap-2">
                <Phone className="h-5 w-5" />
                <span>Call: [Insert Phone Number]</span>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                <span>Email: [Insert Email]</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                <span>Serving all of Ontario</span>
              </div>
            </div>
            
            <Button asChild size="lg" variant="secondary">
              <Link to="/contact">Get Free Flooring Quote</Link>
            </Button>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default FlooringServices;